/**
 * Turns a step frame from the chat stream into something the screen can show.
 *
 * The backend sends identity, never prose: `{type:"thought", tool:"query_databricks_table"}`
 * or `{type:"thought", phase:"thinking"}`, plus `status:"failed"` when the step did not
 * succeed. Writing the sentence is this side's job, which is what puts the progress line in
 * the consultant's language instead of the backend's English.
 *
 * Same shape as the badge config in `bradley-sources-modal.vue`: a map from the backend value
 * to a translation key, resolved at render time, **with a fallback to the raw value**. The
 * fallback is what makes a tool visible the day it is bound rather than the day someone
 * remembers to add a key here — the backend announces every tool it calls, including ones
 * this map has never heard of.
 */

export const BRADLEY_STEP_KEYS = {
  // The six tools the agent binds today (see build_react_agent in the backend). Anything
  // else falls back to its own name, which is the point of the fallback: the backend
  // announces every tool it calls, so this map is a convenience, not a gate.
  tool: {
    azure_document_search: 'newBradley.steps.searchingDocuments',
    analyze_uploaded_documents: 'newBradley.steps.readingAttachments',
    view_page: 'newBradley.steps.lookingAtPage',
    query_uploaded_file: 'newBradley.steps.queryingSpreadsheet',
    describe_databricks_tables: 'newBradley.steps.readingWarehouseSchema',
    query_databricks_table: 'newBradley.steps.queryingWarehouse',
  },
  phase: {
    thinking: 'newBradley.steps.thinking',
  },
}

/** The key for a failed step, whichever tool it was. */
export const BRADLEY_STEP_FAILED_KEY = 'newBradley.steps.failed'

/**
 * @param {{tool?: string|null, phase?: string|null, status?: string|null, text?: string|null}} step
 * @param {(key: string) => string} t vue-i18n's translator
 * @returns {string} the line to show, never empty for a well-formed step
 */
export function resolveBradleyStepLabel(step, t) {
  if (!step) {
    return ''
  }

  if (step.status) {
    // A step only reports again when it failed, so any status is a failure. The tool is named
    // in the message because the last line on screen would otherwise keep describing work
    // that stopped.
    const toolKey = step.tool ? BRADLEY_STEP_KEYS.tool[step.tool] : null
    const what = toolKey ? t(toolKey) : (step.tool ?? '')
    return what ? t(BRADLEY_STEP_FAILED_KEY, { step: what }) : t(BRADLEY_STEP_FAILED_KEY, { step: '' })
  }

  if (step.phase) {
    const phaseKey = BRADLEY_STEP_KEYS.phase[step.phase]
    return phaseKey ? t(phaseKey) : (step.text ?? step.phase)
  }

  if (step.tool) {
    const toolKey = BRADLEY_STEP_KEYS.tool[step.tool]
    // `text` is the backend's fallback label; the raw tool name is the last resort. Neither
    // is translated, and both are better than a blank line.
    return toolKey ? t(toolKey) : (step.text ?? step.tool)
  }

  return step.text ?? ''
}
